import { useEffect, useRef, useState } from "react";
import { Monitor, RefreshCw, Smartphone } from "lucide-react";

const PAGES = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "About", path: "/about" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contact" },
  { label: "Privacy Policy", path: "/privacy-policy" },
  { label: "Terms of Service", path: "/terms-of-service" },
  { label: "Cookie Policy", path: "/cookie-policy" },
];

const PreviewPane = ({ content, path, onPathChange }) => {
  const frameRef = useRef(null);
  const ready = useRef(false);
  const [device, setDevice] = useState("desktop");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === "admin-preview-ready") {
        ready.current = true;
        const frame = frameRef.current;
        if (frame && frame.contentWindow) {
          frame.contentWindow.postMessage({ type: "admin-preview-content", content }, "*");
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [content]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const frame = frameRef.current;
      if (ready.current && frame && frame.contentWindow) {
        frame.contentWindow.postMessage({ type: "admin-preview-content", content }, "*");
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [content]);

  const reload = () => {
    ready.current = false;
    setNonce((value) => value + 1);
  };

  return (
    <aside className="admin-preview" data-testid="admin-preview-pane">
      <div className="admin-preview-bar">
        <select
          className="admin-preview-select"
          value={path}
          onChange={(event) => {
            ready.current = false;
            onPathChange(event.target.value);
          }}
          data-testid="admin-preview-page-select"
        >
          {PAGES.map((page) => (
            <option key={page.path} value={page.path}>
              {page.label}
            </option>
          ))}
        </select>
        <span className="admin-preview-tools">
          <button
            type="button"
            className={device === "desktop" ? "af-icon-btn af-icon-btn-on" : "af-icon-btn"}
            onClick={() => setDevice("desktop")}
            aria-label="Desktop preview"
            data-testid="admin-preview-desktop-btn"
          >
            <Monitor size={13} />
          </button>
          <button
            type="button"
            className={device === "mobile" ? "af-icon-btn af-icon-btn-on" : "af-icon-btn"}
            onClick={() => setDevice("mobile")}
            aria-label="Mobile preview"
            data-testid="admin-preview-mobile-btn"
          >
            <Smartphone size={13} />
          </button>
          <button
            type="button"
            className="af-icon-btn"
            onClick={reload}
            aria-label="Reload preview"
            data-testid="admin-preview-reload-btn"
          >
            <RefreshCw size={13} />
          </button>
        </span>
      </div>
      <div className={device === "mobile" ? "admin-preview-stage admin-preview-mobile" : "admin-preview-stage"}>
        <iframe
          key={`${path}-${nonce}`}
          ref={frameRef}
          className="admin-preview-frame"
          title="Live page preview"
          src={`${path}?preview=1`}
          data-testid="admin-preview-frame"
        />
      </div>
      <p className="admin-preview-note">
        Unsaved edits show here instantly. Nothing is public until you press Save Changes.
      </p>
    </aside>
  );
};

export default PreviewPane;
